/**
 * Who is signed in.
 *
 * `onAuthStateChanged` fires with `null` before it fires with a restored
 * session, so there are three states and not two — signed in, signed out, and
 * *not yet known*. Collapsing the third into "signed out" is what makes an app
 * flash its login screen on every cold start, which on an installed PWA is the
 * first thing the user sees every single morning.
 *
 * THE REDIRECT FLOW NEEDS `authDomain` TO BE THE APP'S OWN DOMAIN. Both halves
 * of sign-in — the popup's hidden iframe and the redirect's handoff — run on
 * `authDomain`, and Safari partitions the storage of an origin that is not the
 * one in the address bar. With the default `<project>.firebaseapp.com` the
 * session written by the handler is written into a partition the app cannot
 * read, so `getRedirectResult` resolves to null and the app returns to its
 * sign-in screen as though nothing happened. See app/.env.example.
 *
 * NOTHING ROLE-RELATED MAY HAPPEN HERE. Sign-in is the one path where a mistake
 * cannot be fixed from inside the app: a user who is bounced during sign-in has
 * no session to fix it with. Membership and roles are resolved in
 * TenantProvider, after there is a session to fall back on.
 */
import {
  browserLocalPersistence,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { createContext, type ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';

import { auth } from '@/firebase.ts';

type AuthState =
  | { status: 'loading'; user: null }
  | { status: 'signed-out'; user: null }
  | { status: 'signed-in'; user: User };

type AuthContextValue = AuthState & {
  /** True from the tap until the session lands or the attempt fails. */
  signingIn: boolean;
  /** Set only for failures the user can act on; a cancelled popup is not one. */
  failed: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Codes that mean "the person changed their mind", not "something is broken".
 *
 * Showing an error for these is worse than showing nothing: the user closed the
 * window deliberately and is then told the app failed, which reads as a bug in
 * the app rather than as the thing they just did.
 */
const CANCELLED = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

/**
 * Codes that mean the popup could not open at all. A redirect is the fallback,
 * and it is not an error either.
 */
const NEEDS_REDIRECT = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
]);

/**
 * iOS, installed to the home screen. `navigator.standalone` is Safari's alone
 * and is true only there — a tab in Safari is false, and every other browser
 * leaves the property undefined.
 *
 * This is the one environment where the popup flow cannot work and does not say
 * so. A standalone window has no popup to give: iOS opens the sign-in in a
 * separate browsing context that never reports back, so Firebase waits for an
 * auth event that will not arrive and then reports `popup-closed-by-user` —
 * which is in CANCELLED, so the app shows nothing at all. The user sees the
 * screen flash and the sign-in button come back, forever.
 *
 * So: redirect first here, and never attempt a popup.
 */
const isIosStandalone = (): boolean =>
  typeof navigator !== 'undefined' && (navigator as { standalone?: boolean }).standalone === true;

const codeOf = (error: unknown): string =>
  typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null });
  const [signingIn, setSigningIn] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Local persistence explicitly, not the default: the default is
    // `browserLocalPersistence` today, but an installed PWA that silently
    // dropped to session persistence would sign the user out every launch, and
    // that is too quiet a failure to leave to a default.
    void setPersistence(auth, browserLocalPersistence);

    // The other half of the redirect fallback below. `onAuthStateChanged` will
    // report the session on its own; this call exists to surface the *failure*
    // of a redirect, which otherwise returns the user to a sign-in screen that
    // acts as though they never tried.
    getRedirectResult(auth).catch((error: unknown) => {
      if (!CANCELLED.has(codeOf(error))) setFailed(true);
    });

    return onAuthStateChanged(auth, (user) => {
      setState(user ? { status: 'signed-in', user } : { status: 'signed-out', user: null });
      setSigningIn(false);
    });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setFailed(false);
    setSigningIn(true);
    try {
      if (isIosStandalone()) {
        // Leaves the page; `signingIn` stays true until it does.
        await signInWithRedirect(auth, new GoogleAuthProvider());
        return;
      }
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (error) {
      const code = codeOf(error);
      if (NEEDS_REDIRECT.has(code)) {
        // Leaves the page; `signingIn` stays true until it does.
        await signInWithRedirect(auth, new GoogleAuthProvider());
        return;
      }
      setSigningIn(false);
      if (!CANCELLED.has(code)) setFailed(true);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signingIn,
      failed,
      signInWithGoogle,
      signOut: async () => {
        await fbSignOut(auth);
      },
    }),
    [state, signingIn, failed, signInWithGoogle],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
};

export const useAuth = (): AuthContextValue => {
  const value = use(AuthContext);
  if (value === null) throw new Error('useAuth outside AuthProvider');
  return value;
};

/** The signed-in user, for code that has already established there is one. */
export const useUser = (): User => {
  const { user } = useAuth();
  if (user === null) throw new Error('No session — guard on auth status first.');
  return user;
};
